// frontend/src/super-admin/billing/ModulePricesPage.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { DataTable } from '../../design-system/components/DataTable';
import { EmptyState } from '../../design-system/components/EmptyState';
import { PageHeader } from '../../design-system/components/PageHeader';
import { useApi } from '../../design-system/useApi';
import { formatDateTime } from '../../design-system/format';
import { VISUAL, statusPill, statusDot } from '../../design-system/visual';
import { Plus, Wallet } from 'lucide-react';
import { billingApi } from '../../modules/platform-billing/api';
import type { ModulePrice } from '../../modules/platform-billing/types';
import { useConfirm } from '../../components/ui/confirm-dialog';

const KNOWN_MODULES = ['invoicing', 'email', 'payments', 'hr', 'payroll', 'monitoring', 'projects', 'expenses', 'esign'];

export function ModulePricesPage() {
  const confirm = useConfirm();
  const { data, loading, reload } = useApi<{ prices: ModulePrice[] }>('/api/admin/billing/module-prices');
  const [draft, setDraft] = React.useState<{ module_key: string; amount: string; seatAmount: string }>({ module_key: '', amount: '', seatAmount: '' });
  const [saving, setSaving] = React.useState(false);

  async function add(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.module_key || !draft.amount) { toast.error('Pick a module and enter an amount'); return; }
    if (draft.seatAmount !== '' && !(Number(draft.seatAmount) >= 0)) { toast.error('Per-seat amount must be ≥ 0'); return; }
    setSaving(true);
    try {
      await billingApi.upsertPrice({
        module_key: draft.module_key,
        amount_per_month: Number(draft.amount),
        ...(draft.seatAmount !== '' ? { seat_amount_per_month: Number(draft.seatAmount) } : {}),
        is_active: true,
      });
      toast.success('Price added');
      setDraft({ module_key: '', amount: '', seatAmount: '' });
      reload();
    } catch (err: any) { toast.error('Add failed', { description: err.message }); }
    finally { setSaving(false); }
  }

  async function toggleActive(p: ModulePrice) {
    try {
      await billingApi.updatePrice(p.id, { is_active: !p.is_active });
      reload();
    } catch (err: any) { toast.error('Update failed', { description: err.message }); }
  }

  async function updateAmount(p: ModulePrice, value: string) {
    const amount = Number(value);
    if (!(amount >= 0)) { toast.error('Amount must be ≥ 0'); return; }
    try {
      await billingApi.updatePrice(p.id, { amount_per_month: amount });
      toast.success('Updated');
      reload();
    } catch (err: any) { toast.error('Update failed', { description: err.message }); }
  }

  async function updateSeatAmount(p: ModulePrice, value: string) {
    const amount = Number(value);
    if (!(amount >= 0)) { toast.error('Per-seat amount must be ≥ 0'); return; }
    try {
      // The PATCH route doesn't carry seat_amount_per_month — go through the
      // upsert (keyed on module_key + currency), which passes it to the service.
      await billingApi.upsertPrice({
        module_key: p.module_key,
        currency: p.currency,
        amount_per_month: Number(p.amount_per_month),
        seat_amount_per_month: amount,
        is_active: p.is_active,
      });
      toast.success('Updated');
      reload();
    } catch (err: any) { toast.error('Update failed', { description: err.message }); }
  }

  async function del(id: string) {
    if (!(await confirm({ title: 'Delete this price?', description: 'Tenants with this module enabled will not be billed for it until you add it back.', destructive: true, confirmLabel: 'Delete' }))) return;
    try {
      await billingApi.deletePrice(id);
      toast.success('Deleted');
      reload();
    } catch (err: any) { toast.error('Delete failed', { description: err.message }); }
  }

  const rows = data?.prices ?? [];

  const available = KNOWN_MODULES.filter((m) => !rows.some((r) => r.module_key === m));

  return (
    <div>
      <PageHeader
        eyebrow="Platform / Billing"
        title="Module prices"
        description="The per-month price tenants pay for each module they enable."
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        {/* Add price section */}
        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Add a price</h3>
          <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-4 items-end">
            <div className="grid gap-1.5">
              <Label>Module</Label>
              <select
                value={draft.module_key}
                onChange={(e) => setDraft({ ...draft, module_key: e.target.value })}
                className="h-10 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">— pick a module —</option>
                {available.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div className="grid gap-1.5">
              <Label>Amount / month <span className="text-muted-foreground font-normal">(USD)</span></Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={draft.amount}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })}
                placeholder="25.00"
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Per seat / month <span className="text-muted-foreground font-normal">(USD, optional)</span></Label>
              <Input
                type="number"
                min={0}
                step="0.01"
                value={draft.seatAmount}
                onChange={(e) => setDraft({ ...draft, seatAmount: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <Button type="submit" disabled={saving || available.length === 0} className="rounded-full h-10 px-5 text-[13px] font-semibold gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              {saving ? 'Adding…' : 'Add price'}
            </Button>
          </form>
        </section>

        {/* Prices table */}
        <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
          <DataTable<ModulePrice>
            loading={loading}
            rows={rows}
            rowKey={(r) => r.id}
            columns={[
              { key: 'module', header: 'Module', cell: (r) => <span className="font-mono text-[13px]">{r.module_key}</span> },
              { key: 'amount', header: 'Amount / month', cell: (r) => (
                <Input
                  type="number" min={0} step="0.01"
                  defaultValue={r.amount_per_month}
                  className="w-32 h-9 rounded-lg"
                  onBlur={(e) => {
                    // Numeric compare — '25' vs the stored '25.00' string is not a change.
                    if (Number(e.target.value) !== Number(r.amount_per_month)) updateAmount(r, e.target.value);
                  }}
                />
              ) },
              { key: 'seat', header: 'Per seat / month', cell: (r) => (
                <Input
                  type="number" min={0} step="0.01"
                  defaultValue={r.seat_amount_per_month ?? '0.00'}
                  className="w-32 h-9 rounded-lg"
                  onBlur={(e) => {
                    if (Number(e.target.value) !== Number(r.seat_amount_per_month ?? 0)) updateSeatAmount(r, e.target.value);
                  }}
                />
              ) },
              { key: 'currency', header: 'Currency', cell: (r) => r.currency, className: 'text-muted-foreground' },
              { key: 'status', header: 'Status', cell: (r) => (
                <span className={statusPill(r.is_active ? 'success' : 'muted')}>
                  <span className={statusDot(r.is_active ? 'success' : 'muted')} />
                  {r.is_active ? 'Active' : 'Inactive'}
                </span>
              ) },
              { key: 'updated', header: 'Updated', cell: (r) => formatDateTime(r.updated_at), className: 'text-xs text-muted-foreground' },
              { key: 'actions', header: '', cell: (r) => (
                <div className="flex gap-1 justify-end">
                  <Button size="sm" variant="ghost" className="rounded-full h-8" onClick={() => toggleActive(r)}>{r.is_active ? 'Disable' : 'Enable'}</Button>
                  <Button size="sm" variant="ghost" className="rounded-full h-8 text-muted-foreground hover:text-destructive" onClick={() => del(r.id)}>Delete</Button>
                </div>
              ) },
            ]}
            emptyState={<EmptyState icon={<Wallet />} title="No prices yet" description="Add a module price above. Tenants with unset modules are not billed for them." />}
          />
        </section>
      </div>
    </div>
  );
}
